import { getAuthHeaders } from '../../utils/token';
import { api } from '../Users/user';

const BASE_URL = api + '/api';

// --- Tipos para Modalidad (Modality) ---

export interface ModalityAPI {
  _id: string;
  name: string;
  dicom_code: string;
  description?: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ModalityData {
  name: string;
  dicom_code: string;
  description?: string;
  status: boolean;
}

// --- Tipos para Equipamiento (Equipment) ---

export interface EquipmentAPI {
  _id: string;
  name: string;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EquipmentData {
  name: string;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  status: boolean;
}

// --- Tipos para Servicio (Service) ---

// Tipos para las referencias pobladas en ServiceAPI
export interface BranchReference {
  _id: string;
  name: string;
  short_name: string;
}
export interface ModalityReference {
  _id: string;
  name: string;
  dicom_code: string;
}
export interface EquipmentReference {
  _id: string;
  name: string;
  model: string;
}

export interface ServiceAPI {
  _id: string;
  fk_branch: BranchReference;
  fk_modality: ModalityReference;
  fk_equipments: EquipmentReference[];
  name: string;
  status: boolean;
  calendar_settings: {
    default_duration_min: number;
    max_patients_per_slot: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ServiceData {
  fk_branch: string; // ID de MongoDB de la Sucursal
  fk_modality: string; // ID de MongoDB de la Modalidad
  fk_equipments: string[]; // Array de IDs de MongoDB de los Equipos
  name: string;
  status: boolean;
  calendar_settings?: {
    default_duration_min?: number;
    max_patients_per_slot?: number;
  };
}


// --- Función de Utilidad para Fetch con Manejo de Errores (Reutilizada) ---
async function apiFetch<T>(url: string, options: RequestInit): Promise<T> {
  const defaultHeaders = getAuthHeaders();
  options.headers = {
    ...defaultHeaders,
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(url, options);
  const data = await response
    .json()
    .catch(() => ({ message: response.statusText || 'Error desconocido' }));

  if (!response.ok) {
    throw new Error(data.message || `La petición falló con status ${response.status}`);
  }

  return data as T;
}

// --- Funciones CRUD para Modalidades ---

export function fetchAllModalities(): Promise<ModalityAPI[]> {
  return apiFetch<ModalityAPI[]>(`${BASE_URL}/modalities`, { method: 'GET' });
}

export function createModality(data: ModalityData): Promise<ModalityAPI> {
  return apiFetch<ModalityAPI>(`${BASE_URL}/modalities`, { method: 'POST', body: JSON.stringify(data) });
}

export function updateModality(id: string, data: Partial<ModalityData>): Promise<ModalityAPI> {
  return apiFetch<ModalityAPI>(`${BASE_URL}/modalities/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteModality(id: string): Promise<void> {
  return apiFetch<void>(`${BASE_URL}/modalities/${id}`, { method: 'DELETE' });
}

// --- Funciones CRUD para Equipamiento ---

export function fetchAllEquipments(): Promise<EquipmentAPI[]> {
  return apiFetch<EquipmentAPI[]>(`${BASE_URL}/equipments`, { method: 'GET' });
}

export function createEquipment(data: EquipmentData): Promise<EquipmentAPI> {
  return apiFetch<EquipmentAPI>(`${BASE_URL}/equipments`, { method: 'POST', body: JSON.stringify(data) });
}

export function updateEquipment(id: string, data: Partial<EquipmentData>): Promise<EquipmentAPI> {
  return apiFetch<EquipmentAPI>(`${BASE_URL}/equipments/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteEquipment(id: string): Promise<void> {
  return apiFetch<void>(`${BASE_URL}/equipments/${id}`, { method: 'DELETE' });
}

// --- Funciones CRUD para Servicios ---

export function fetchAllServices(): Promise<ServiceAPI[]> {
  return apiFetch<ServiceAPI[]>(`${BASE_URL}/services`, { method: 'GET' });
}

export function createService(data: ServiceData): Promise<ServiceAPI> {
  return apiFetch<ServiceAPI>(`${BASE_URL}/services`, { method: 'POST', body: JSON.stringify(data) });
}

export function updateService(id: string, data: Partial<ServiceData>): Promise<ServiceAPI> {
  return apiFetch<ServiceAPI>(`${BASE_URL}/services/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteService(id: string): Promise<void> {
  return apiFetch<void>(`${BASE_URL}/services/${id}`, { method: 'DELETE' });
}
